import { PaletteItem, PaletteSection } from './types';

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function scoreItem(item: PaletteItem, query: string, translate?: (key: string) => string): number {
  if (!query) {
    return 1;
  }

  const q = normalize(query);
  const label = translate && item.kind === 'page'
    ? normalize(String(translate(item.label)))
    : normalize(item.label);
  const description = normalize(item.description || '');
  const keywords = (item.keywords || []).map(normalize);
  const haystack = [label, description, ...keywords, normalize(item.path || '')].join(' ');

  if (label === q || description === q) {
    return 100;
  }
  if (label.startsWith(q) || description.startsWith(q)) {
    return 80;
  }
  if (label.includes(q) || description.includes(q)) {
    return 60;
  }
  if (keywords.some((keyword) => keyword.includes(q))) {
    return 40;
  }
  if (haystack.includes(q)) {
    return 20;
  }
  return 0;
}

export function isPrimaryPage(item: PaletteItem): boolean {
  const key = normalize(String((item.meta && item.meta.titleKey) || item.label || ''));
  const path = normalize(item.path || '');
  if (key.includes('create') || key.includes('_add') || /(^|_)add$/.test(key)) {
    return false;
  }
  if (path.includes('/create') || /\/add$/.test(path) || path.endsWith('/add')) {
    return false;
  }
  return true;
}

export function filterPages(
  pages: PaletteItem[],
  query: string,
  translate?: (key: string) => string,
  limit = 5,
): PaletteItem[] {
  const q = normalize(query);
  if (!q) {
    // Default view: list/browse destinations only (DNS, SSL, vhost, …) so the grid fits without scroll.
    // Create/add routes remain available when searching.
    return pages.filter(isPrimaryPage);
  }

  return pages
    .map((item) => ({ item, score: scoreItem(item, q, translate) }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((row) => row.item);
}

export function filterServers(servers: PaletteItem[], query: string, limit = 5): PaletteItem[] {
  const q = normalize(query);
  if (!q) {
    return [];
  }

  return servers
    .map((item) => ({ item, score: scoreItem(item, q) }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((row) => row.item);
}

export function filterRecent(recent: PaletteItem[], query: string, limit = 5): PaletteItem[] {
  const q = normalize(query);
  if (!q) {
    return recent.slice(0, limit);
  }

  return recent
    .map((item) => ({ item, score: scoreItem(item, q) }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((row) => row.item);
}

export function buildConsoleBridgeItems(query: string, hasWebsiteScope: boolean): PaletteItem[] {
  if (!hasWebsiteScope) {
    return [];
  }

  const q = normalize(query);
  const items: PaletteItem[] = [];

  if (!q || 'console'.startsWith(q) || 'command'.startsWith(q) || q.includes('console')) {
    items.push({
      id: 'console:open',
      kind: 'console',
      label: 'Website command console',
      description: '/websites/console',
      icon: 'mdi-console',
      routeName: 'website_console',
      keywords: ['console', 'command', 'terminal', 'slash'],
    });
  }

  const trimmed = query.trim();
  if (trimmed.length >= 2 && !trimmed.startsWith('/')) {
    const looksLikeEntity = /^[\w.-]+$/.test(trimmed) || /^\d+$/.test(trimmed);
    if (looksLikeEntity && (q.includes('.') || /^\d+$/.test(trimmed) || q.length >= 4)) {
      items.push({
        id: `console:site:${trimmed}`,
        kind: 'console',
        label: `Open in console: /site ${trimmed}`,
        description: 'Website command console',
        icon: 'mdi-console-line',
        routeName: 'website_console',
        routeQuery: { command: `/site ${trimmed}` },
        keywords: ['console', 'site', trimmed.toLowerCase()],
      });
    }
  }

  return items.slice(0, 5);
}

export function flattenSections(sections: PaletteSection[]): PaletteItem[] {
  const flat: PaletteItem[] = [];
  sections.forEach((section) => {
    section.items.forEach((item) => flat.push(item));
  });
  return flat;
}

/**
 * Compact browse label: "SSL · Certificates", "System Users · List".
 * Avoids repeating the group when the title already includes it.
 */
export function browsePageChipLabel(
  item: PaletteItem,
  translate: (key: string) => string,
): string {
  const group = String((item.meta && item.meta.groupLabel) || '').trim();
  const titleKey = String((item.meta && item.meta.titleKey) || item.label || '');
  const title = translate(titleKey) || titleKey;
  if (!group) {
    return title;
  }
  const groupNorm = group.toLowerCase();
  const titleNorm = title.toLowerCase();
  if (titleNorm.indexOf(groupNorm) === 0 || groupNorm.indexOf(titleNorm) === 0) {
    return title;
  }
  return `${group} · ${title}`;
}
